import { notifyError } from "../components/Toast";
import { getDefaultFechaIngreso, getPeriodoFromFecha } from "../hooks/useAvailableDates";
import { stepIds } from "../enums/fieldsDataConfig";

// Carga inicial de carreras desde CheckoutPage (fase de carga, antes de que el form monte), para
// que el paso de programa arranque con el combo lleno. La fecha es la misma que el form va a
// mostrar (getDefaultFechaIngreso): la guardada en redux si hay, si no la del link o la primera.
// Es un thunk porque la fecha guardada se lee del store en el momento de ejecutar: sin PersistGate,
// la rehidratacion de sessionStorage puede llegar despues del primer render de CheckoutPage.
// Primero se piden las fechas de ingreso de la entidad (T-2026-158: los colegios ingresan en marzo).
export const CargarCarrerasIniciales = ({ fi, t, e }) => {
  return async (dispatch, getState) => {
    await dispatch(CargarFechasIngreso(e));

    const steps = getState().respuestas?.steps || {};
    const programa = Object.values(steps).find((s) => s.id === stepIds.PROGRAMA);
    const fechaGuardada = programa?.responses?.fechaIngreso || null;

    const fecha = getDefaultFechaIngreso(fi, t, fechaGuardada, getState().carreras.fechasIngreso);
    if (!fecha) return;
    return dispatch(GetCarreras(fecha, getPeriodoFromFecha(fecha), t, e));
  };
};

// Fechas de ingreso que ofrece NBAPI para la entidad (yyyy-MM-dd). Nunca rechaza: ante error queda
// fechasIngreso.error y useAvailableDates vuelve al calendario anterior (abril/agosto), asi el
// checkout no se traba si el endpoint no esta (p. ej. front publicado antes que NBAPI).
export const CargarFechasIngreso = (entidad = 'UAI') => {
  return async (dispatch) => {
    dispatch({ type: 'LOAD_FECHAS_INGRESO_REQUEST' });

    try {
      const params = new URLSearchParams({ entidad: entidad || 'UAI' });
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/checkout/fechasIngreso?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'tokenCarreras': process.env.REACT_APP_TOKEN_CARRERAS,
          },
        }
      );

      if (response.ok) {
        const fechas = await response.json();
        dispatch({ type: 'LOAD_FECHAS_INGRESO_SUCCESS', payload: Array.isArray(fechas) ? fechas : [] });
      } else {
        dispatch({ type: 'LOAD_FECHAS_INGRESO_FAIL', payload: await response.text() });
      }
    } catch (error) {
      dispatch({ type: 'LOAD_FECHAS_INGRESO_FAIL', payload: error.message });
    }
  };
};

// El front manda la intencion (t = g/p/e/m) y la entidad; el ciclo de SIGUE (UN, UP, CU...)
// lo resuelve NBAPI segun la entidad. Antes se calculaba aca y solo servia para UAI.
// Se muestran todas las carreras-plan que devuelve SIGUE, como hacía el formulario de Umbraco: si
// hay dos planes habilitados de la misma carrera aparecen los dos, distinguidos por el código de
// plan en la etiqueta. Antes se dejaba sólo el plan más nuevo (decisión de Matías, 05/10/2026).
export const GetCarreras = (fecha, periodo, t, entidad = 'UAI') => {
  return async (dispatch) => {
    dispatch({ type: 'LOAD_CARRERAS_REQUEST' });


    try {
      const params = new URLSearchParams({ fecha, tipo: t, periodo, entidad });
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/checkout/getCarreras?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'tokenCarreras': process.env.REACT_APP_TOKEN_CARRERAS,
          },
        }
      );

      if (response.ok) {
        const carreras = await response.json();

        // Lógica para agrupar y transformar los datos
        const groupedDataCareers = carreras
          .filter((carrera) => carrera.c_tipo_id === 'F') // Filtrar facultades
          .map((facultad) => {
            // Carreras de la facultad, todas las que devuelve SIGUE
            const careers = carreras
              .filter(
                (carrera) =>
                  carrera.c_codigo_id.startsWith(facultad.c_codigo_id) && carrera.c_tipo_id === 'C'
              )
              .map((carrera) => ({
                cod: carrera.c_codigo_id,
                description: carrera.vc_descripcion
                  .replace(carrera.c_codigo_id, '')
                  .trim()
                  .charAt(0)
                  .toUpperCase() + carrera.vc_descripcion
                  .replace(carrera.c_codigo_id, '')
                  .trim()
                  .slice(1)
                  .toLowerCase(),
                space: carrera.vc_cupo,
                mode: carrera.c_modalidad_id,
                modalidad: carrera.vc_modalidad || null,
              }));

            return {
              cod: facultad.c_codigo_id,
              description: facultad.vc_descripcion,
              careers,
            };
          });


        dispatch({
          type: 'LOAD_CARRERAS_SUCCES',
          payload: groupedDataCareers,
        });
      } else {
        const errorMessage = await response.text();
        dispatch({ type: 'LOAD_CARRERAS_FAIL', payload: errorMessage });
        notifyError('Hubo un error al cargar las carreras');
      }
    } catch (error) {
      dispatch({ type: 'LOAD_CARRERAS_FAIL', payload: error.message });
    }
  };
};

export const GetCampusDisp = (ano, carrera, plan, periodo, entidad = 'UAI') => {
  return async (dispatch) => {
    dispatch({ type: 'LOAD_CAMPUS_REQUEST' });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/checkout/getInscripcionTemporal?anioCalendario=${ano}&carrera=${carrera}&plan=${plan}&periodo=${periodo}&entidad=${entidad}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'tokenCarreras': process.env.REACT_APP_TOKEN_CARRERAS,
          },
        }
      );
      if (response.ok) {
        const campus = await response.json();
        dispatch({
          type: 'LOAD_CAMPUS_SUCCES',
          payload: campus,
        });
      } else {
        const errorMessage = await response.text();
        dispatch({ type: 'LOAD_CAMPUS_FAIL', payload: errorMessage });
        notifyError('Hubo un error al cargar los campus');
      }
    } catch (error) {
      dispatch({ type: 'LOAD_CAMPUS_FAIL', payload: error.message });
    }
  };
};

export const GetTurnosDisp = (ano, carrera, plan, anioCursada, localizacion, periodo, entidad = 'UAI') => {
  return async (dispatch) => {
    // Verificar si alguno de los parámetros es null o undefined
    if (!ano || !carrera || !plan || !anioCursada || !localizacion) {
      console.warn("Parámetros inválidos para GetTurnosDisp:", {
        ano,
        carrera,
        plan,
        anioCursada,
        localizacion,
      });
      return; // No hacer nada si algún parámetro es inválido
    }

    dispatch({ type: 'LOAD_TURNOS_REQUEST' });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/checkout/getTurnos?carrera=${carrera}&fecha=${ano}&localizacion=${localizacion}&plan=${plan}&anio=${anioCursada}&periodoIng=${periodo}&entidad=${entidad}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'tokenCarreras': process.env.REACT_APP_TOKEN_CARRERAS,
          },
        }
      );
      if (response.ok) {
        const turnos = await response.json();
        dispatch({
          type: 'LOAD_TURNOS_SUCCES',
          payload: turnos,
        });
      } else {
        if (response.status === 404) {
          dispatch({ type: 'LOAD_TURNOS_FAIL', payload: 'No hay turnos disponibles' });
          
        } else {
          dispatch({ type: 'LOAD_TURNOS_FAIL', payload: await response.text() });
          
          const customErrorMessage = 'Ha ocurrido un errror al cargar los turnos disponibles';
          notifyError(customErrorMessage);
        }
      }
    } catch (error) {
      dispatch({ type: 'LOAD_TURNOS_FAIL', payload: error.message });
    }
  };
};

export const GetValor = (ano, carrera, plan, anioCursada, localizacion, turno, formaPago,fechaIngreso,tipoAsistente,tipoBonificacion = null, entidad = 'UAI') => {
  return async (dispatch) => {
    // Cada pedido va a su propio slot segun la bonificacion: 'alumnos' -> comunidad.
    const slot = tipoBonificacion ? 'comunidad' : 'normal';
    dispatch({ type: 'LOAD_VALOR_REQUEST', slot });
    try {

      let fechaIngresoFinal = fechaIngreso;

      if (fechaIngreso) {
        const [year, month] = fechaIngreso.split('-');
        const mes = Number(month);

        if (mes >= 1 && mes <= 7) {
          fechaIngresoFinal = `${year}-04-01`;
        } else if (mes >= 8) {
          fechaIngresoFinal = `${year}-08-01`;
        }
      }

      const params = new URLSearchParams({
        carrera,
        fecha: ano,
        localizacion,
        turno,
        formaPago,
        plan,
        anio: anioCursada,
        fechaIngreso: fechaIngresoFinal,
        tipoAsistente,
        tipoBonificacion : tipoBonificacion || "",
        entidad,
      });
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/checkout/getCoste?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'tokenCarreras': process.env.REACT_APP_TOKEN_CARRERAS,
          },
        }
      );
      if (response.ok) {
        const valor = await response.json();
        dispatch({
          type: 'LOAD_VALOR_SUCCES',
          payload: valor,
          slot,
        });
      } else {
        const errorMessage = await response.text();
        dispatch({ type: 'LOAD_VALOR_FAIL', payload: errorMessage, slot });
        notifyError('Hubo un error al cargar el valor');
      }
    } catch (error) {
      dispatch({ type: 'LOAD_VALOR_FAIL', payload: error.message, slot });
    }
  };
};


export const GetAnioCarrera = (carrera, plan, entidad = 'UAI') => {
  return async (dispatch) => {
    if (!carrera || !plan) {
      notifyError("El plan u carrera no pueden estar vacío.");
      return;
    }

    dispatch({ type: "LOAD_ANIOCARRERA_REQUEST" });
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/checkout/carrera/anios?carrera=${carrera}&plan=${plan}&entidad=${entidad}`,
        {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const formattedData = data.map((item) => {
          const value = Object.values(item)[0]; // Obtiene el primer valor del objeto
          return {
            value: Number(value), // Convertimos a número para evitar problemas
            label: String(value),
          };
        });
        dispatch({ type: "LOAD_ANIOCARRERA_SUCCESS", payload: formattedData });
      } else {
        const errorMessage = await response.text();
        dispatch({ type: "LOAD_ANIOCARRERA_FAIL", payload: errorMessage });
        notifyError("Hubo un error al cargar la información del CUE.");
      }
    } catch (error) {
      dispatch({ type: "LOAD_ANIOCARRERA_FAIL", payload: error.message });
      notifyError("Error de conexión al cargar la información del CUE.");
    }
  };
};

export const GetRequerimientosMicrocredencial = () => {
  return async (dispatch) => {
    dispatch({ type: 'LOAD_REQUERIMIENTOS_REQUEST' });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/checkout/microcredencial/requerimiento`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const requerimientos = await response.json();
        dispatch({
          type: 'LOAD_REQUERIMIENTOS_SUCCESS',
          payload: requerimientos,
        });
      } else {
        const errorMessage = await response.text();
        dispatch({ type: 'LOAD_REQUERIMIENTOS_FAIL', payload: errorMessage });
        notifyError('Hubo un error al cargar los requerimientos de microcredenciales');
      }
    } catch (error) {
      dispatch({ type: 'LOAD_REQUERIMIENTOS_FAIL', payload: error.message });
      notifyError('Error de conexión al cargar los requerimientos de microcredenciales');
    }
  };
};

// Limpia ambos slots de precio (usar al cambiar fecha/carrera/campus/año).
export const ClearValor = () => ({ type: 'CLEAR_VALOR' });
